/*
 * The cell container is a component to hold state for an individual contained component (e.g. text field that holds currency)
 * This state eventually is propagated up to a parent.  In order to do this, the parent needs to know which cell is
 * reporting the data change.  If cell location is always fixed (e.g. a spreadsheet cell has x and y coordinates that are
 * immutable), coordinates can be used.  However, if the cell itself can move (e.g. the row it is in can be re-ordered),
 * it needs to return an identifier that the parent can use to locate the cell.
 * 
 * In this implementation, the container is referenced by unique keys.
 */
import React, { Component } from 'react';
import CurrencyNumberFormat from './CurrencyNumberFormat';
import PercentageNumberFormat from './PercentageNumberFormat';
import PropTypes from 'prop-types';
import { TextField } from '@mui/material';

class CellContainer extends Component {
  constructor(props) {
    super(props);
    this.state = {
      value: props.value,
    }
  }

  // User has changed value on the field.
  handleChange = (e) => {
    this.setState({value: e.target.value});
  }

  // User has left the field
  handleBlur = (e) => {
    // Use the value that is on the state - and only need to pass it up
    // if it is different from the original props value.
    if (this.state.value !== this.props.value) {
      this.hasNewValue(this.state.value);
    }
  }

  // User has hit enter on the field
  handleKeyPress = (e) => {
    if (e.key === 'Enter' && this.state.value !== this.props.value) {
      this.hasNewValue(this.state.value);
    }
  }

  // We're done changing values on this field, so propagate the value to
  // the parent. Called from "onBlur" (user has left the field) and "onKeyPress" (user has hit enter while on the field)
  // y represents the row index
  // x represents the column index
  //
  // 2020/10/16: If in error, don't propagate
  hasNewValue = (value) => {
    if (! this.isFieldError()) {
      this.props.onChangedValue(
        this.props.cellKey,
        this.props.rowKey,
        value
      );
    } else {
      this.setState({value: this.props.value});
    }
  }

  isFieldError = () => {
    if (this.props.isError) {
      return this.props.isError(this.props.cellKey, this.state.value);
    }
    return false;
  }

  getFieldHelperText = () => {
    if (this.props.fieldHelper) {
      return this.props.fieldHelper(this.props.cellKey, this.state.value);
    }
    return '';
  }

  render() {
    if (this.props.fieldtype === 'currency') {
      return (
        <TextField
          id={this.props.id}
          label={this.props.label}
          value={this.state.value}
          onChange={(e) => this.handleChange(e)}
          onBlur={(e) => this.handleBlur(e)}
          onKeyPress={(e) => this.handleKeyPress(e)}
          InputProps={{
            inputComponent: CurrencyNumberFormat
          }}
          inputProps={{
            minValue: this.props.minValue,
            maxValue: this.props.maxValue,
            style: { textAlign: "right" }
          }}
          error={this.isFieldError()}
          helperText={this.getFieldHelperText()}
        />
      );
    }

    if (this.props.fieldtype === 'percentage') {
      return (
        <TextField
          id={this.props.id} 
          label={this.props.label}
          value={this.state.value}
          onChange={(e) => this.handleChange(e)}
          onBlur={(e) => this.handleBlur(e)}
          onKeyPress={(e) => this.handleKeyPress(e)}
          InputProps={{
            inputComponent: PercentageNumberFormat
          }}
          inputProps={{
            minValue: 0,
            maxValue: 100,
            style: { textAlign: "right" }
          }}
          error={this.isFieldError()}
          helperText={this.getFieldHelperText()}
        />
      );
    }

    return (
      <TextField
        id={this.props.id}
        label={this.props.label}
        value={this.state.value}
        onChange={(e) => this.handleChange(e)}
        onBlur={(e) => this.handleBlur(e)}
        onKeyPress={(e) => this.handleKeyPress(e)}
        error={this.isFieldError()}
        helperText={this.getFieldHelperText()}
      />
    );
  }
}
   
CellContainer.propTypes = {
  fieldtype: PropTypes.string.isRequired,
  id: PropTypes.string.isRequired,
  onChangedValue: PropTypes.func.isRequired,
};
    
export default CellContainer;
  